import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useTable } from "react-table";
import { FaMinusCircle, FaPlusCircle } from "react-icons/fa";
import FundedDatePicker from "../../../../datepicker/FundedDatePicker";
import { showAlert, showToast } from "../../../../../utils/utils";
import { getColumns } from "./columns";
import PropTypes from "prop-types";
import "./fundinvtable.scss";
import {
  alertActions,
  fundedInvoicesData,
  profiletexts,
  repaymentDateAlert
} from "../../../../../locales/en";
import {
  CURRENCY_FORMAT,
  showToastType
} from "../../../../../utils/constants/appConstants";
import {
  getInvoiceByIdsURL,
  getSetRepaymentDateUrl
} from "../../../../../utils/constants/apiEndPoints";
import privateAxios from "../../../../../utils/axiosPrivate";

// Blank amounts show as $0.00 rather than an empty cell
const formatAmount = (value) => {
  if (value === undefined || value === null || value === "") {
    return "$0.00";
  }
  return `$${value.toFixed(2)}`;
};

// The expanded (mobile card) view derives several amounts from sibling fields
const renderExpandedAmount = (row, accessor) => {
  if (accessor === "feeAmount") {
    const feeAmount = Number(row.original.feeAmount || 0);
    const feeSaved = Number(row.original.feeSaved || 0);
    return `$${(feeAmount + feeSaved).toFixed(2)}`;
  }

  if (accessor === "advanceAmount" || accessor === "feeSaved") {
    return formatAmount(row.original[accessor]);
  }

  if (accessor === "totalFee") {
    const fee =
      row.original.totalFee && row.original.totalFee !== 0
        ? row.original.totalFee
        : row.original.feeAmount;

    if (fee === undefined || fee === null || fee === "") {
      return "$0.00";
    }
    return `$${parseFloat(fee).toFixed(2)}`;
  }

  return row.original[accessor];
};

const FundInvTable = (props) => {
  const {
    selectInvoices,
    setSelectedInvData,
    refferalParrtner,
    promoCodeSubmit,
    quotedData,
    setQuotedData,
    setPersistentSelectedRows,
    loadInvoices
  } = props;
  const [expandedRows, setExpandedRows] = useState([]);
  const columns = useMemo(
    () => getColumns(refferalParrtner, promoCodeSubmit),
    [refferalParrtner, promoCodeSubmit]
  );

  // for quoted data
  const loadInvoiceQuoteData = useCallback(async () => {
    try {
      const selectedInvoicesRequestBody = [];
      selectInvoices.forEach((invoice) => {
        selectedInvoicesRequestBody.push({
          invoiceId: invoice.id,
          invoiceRepaymentDate: invoice.dueDate
        });
      });
      const response = await privateAxios.post(getInvoiceByIdsURL, {
        invoiceDetails: selectedInvoicesRequestBody
      });
      const responseData = response.data.data;

      // Add a check to ensure responseData and responseData.invoices are defined
      if (responseData && responseData.invoices) {
        setQuotedData(responseData.invoices);
        setSelectedInvData(responseData.invoices);
      } else {
        console.error(profiletexts.errorMessage.invoiceResponseError);
      }
    } catch {
      // handle catch here;
    }
  }, [privateAxios, selectInvoices, setSelectedInvData]);

  useEffect(() => {
    loadInvoiceQuoteData();
  }, [loadInvoiceQuoteData]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({
      columns,
      data: quotedData
    });

  const toggleRow = useCallback((rowId) => {
    setExpandedRows((prevState) =>
      prevState.includes(rowId)
        ? prevState.filter((id) => id !== rowId)
        : [...prevState, rowId]
    );
  }, []);

  const initialShowNewDateButton = false;
  // Reayment Date Change Function Start
  const [showNewDateButton, setShowNewDateButton] = useState(
    initialShowNewDateButton
  );
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  const handleRepayDate = async (date, rowId) => {
    const formattedDate = date ? date.toLocaleDateString("en-CA") : "";
    setSelectedInvoiceId(rowId);
    setSelectedDate(formattedDate);
    setShowNewDateButton((prevState) => ({ ...prevState, [rowId]: true }));
  };

  const handleNewDateButtonClick = async () => {
    if (!selectedInvoiceId || !selectedDate) return;

    const result = await showAlert(
      repaymentDateAlert?.title,
      repaymentDateAlert?.subtitle,
      alertActions?.warning,
      alertActions?.Confirm,
      alertActions?.Cancel
    );

    if (!result.isConfirmed) return;

    try {
      const invoiceToUpdate =
        quotedData.find((inv) => inv.id === selectedInvoiceId) ||
        selectInvoices.find((inv) => inv.id === selectedInvoiceId);

      const dataToSend = {
        ...invoiceToUpdate,
        settlementDate: selectedDate
      };

      const response = await privateAxios.put(
        getSetRepaymentDateUrl(selectedInvoiceId),
        dataToSend
      );

      if (response.status === 200) {
        showToast(response.data.message, showToastType?.success);
        setShowNewDateButton((prevState) => ({
          ...prevState,
          [selectedInvoiceId]: false
        }));

        if (setPersistentSelectedRows) {
          setPersistentSelectedRows((prev) =>
            prev.map((inv) =>
              inv.id === selectedInvoiceId
                ? { ...inv, dueDate: selectedDate }
                : inv
            )
          );
          const targetInvoice = selectInvoices.find(
            (inv) => inv.id === selectedInvoiceId
          );
          if (targetInvoice) {
            targetInvoice.dueDate = selectedDate;
          }

          const invoiceDetails = selectInvoices.map((invoice) => ({
            invoiceId: invoice.id,
            invoiceRepaymentDate:
              invoice.id === selectedInvoiceId ? selectedDate : invoice.dueDate
          }));

          try {
            const quoteResponse = await privateAxios.post(getInvoiceByIdsURL, {
              invoiceDetails
            });

            if (quoteResponse.status === 200) {
              const responseData = quoteResponse.data.data;
              if (responseData && responseData.invoices) {
                setQuotedData(responseData.invoices);
                setSelectedInvData(responseData.invoices);
              }
            }
          } catch (quoteError) {
            console.error(quoteError);
          }

          if (loadInvoices) {
            loadInvoices();
          }

          setSelectedInvoiceId(null);
          setSelectedDate(null);
        }
      }
    } catch (error) {
      showToast(error?.response?.data?.message, showToastType?.error);
    }
  };

  const getSelectedDueDate = (row) => {
    if (selectedInvoiceId === row.original.id && selectedDate) {
      return new Date(selectedDate);
    }
    if (row.original.settlementDate) {
      return new Date(row.original.settlementDate);
    }
    return new Date(row.original.dueDate);
  };

  const renderCellContent = (cell, row) => {
    if (cell.column.id === "dueDate") {
      return (
        <>
          <FundedDatePicker
            selected={getSelectedDueDate(row)}
            originalDueDate={new Date(row.original?.originalDueDate)}
            isExtendedRepaymentDateValidation={
              row.original?.isExtendedRepaymentDateValidation
            }
            onChange={(date) => handleRepayDate(date, row.original.id)}
          />
          {showNewDateButton[row.original.id] && (
            <button
              className="btn btn-primary new-pay-date mt-1"
              onClick={handleNewDateButtonClick}
            >
              {fundedInvoicesData.funded.newDate}
            </button>
          )}
        </>
      );
    }

    if (cell.column.id === "feeAmount") {
      return (
        <>
          {CURRENCY_FORMAT}
          {row.original.feeAmount + row.original.feeSaved}
        </>
      );
    }

    return cell.render("Cell");
  };

  return (
    <table className="mb-3 fundinvtable" {...getTableProps()}>
      <thead>
        {headerGroups.map((headerGroup) => {
          const { key, ...rest } = headerGroup.getHeaderGroupProps();
          return (
            <tr className="parentrow" {...rest} key={key}>
              <th />
              {headerGroup.headers.map((column) => (
                <th key={column.id}>
                  <span>{column.render("Header")}</span>
                </th>
              ))}
            </tr>
          );
        })}
      </thead>

      <tbody {...getTableBodyProps()}>
        {rows.map((row) => {
          prepareRow(row);
          const isExpanded = expandedRows.includes(row.id);

          return (
            <React.Fragment key={row.id}>
              <tr className="parentrow" {...row.getRowProps()}>
                <td className="expand-td">
                  <span
                    className="expandbtn cursor-pointer"
                    onClick={() => toggleRow(row.id)}
                  >
                    {isExpanded ? <FaMinusCircle /> : <FaPlusCircle />}
                  </span>
                </td>
                {row.cells.map((cell) => (
                  <td key={cell.getCellProps().key} {...cell.getCellProps()}>
                    {renderCellContent(cell, row)}
                  </td>
                ))}
              </tr>
              {isExpanded && (
                <tr className="childrow colexpand">
                  <td colSpan={columns.length + 2}>
                    <div className="tdbody">
                      {columns.map((column) => {
                        return (
                          <span
                            key={column.accessor}
                            data-label={column.Header}
                          >
                            {renderExpandedAmount(row, column.accessor)}
                          </span>
                        );
                      })}
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
};

FundInvTable.propTypes = {
  selectInvoices: PropTypes.array.isRequired,
  setSelectedInvData: PropTypes.func.isRequired,
  refferalParrtner: PropTypes.any,
  promoCodeSubmit: PropTypes.any,
  quotedData: PropTypes.any,
  setQuotedData: PropTypes.any,
  setPersistentSelectedRows: PropTypes.func,
  loadInvoices: PropTypes.func
};

export default FundInvTable;
